<template>
  <div>
    <!-- Dialog para visualizar logs -->
    <v-dialog v-model="internalDialog" max-width="85vw" scrollable class="modern-dialog">
      <v-card class="modern-dialog-card">
        <v-card-title class="modern-dialog-title">
          <v-icon class="mr-2" color="primary">mdi-file-document-outline</v-icon>
          <span class="text-h6 font-weight-bold">Visualizador de Logs</span>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn 
                icon
                color="primary"
                class="mr-2"
                @click="copiarLinkCompartilhavel"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-share-variant</v-icon>
              </v-btn>
            </template>
            <span>Copiar link compartilhável</span>
          </v-tooltip>
          <v-btn icon @click="closeDialog" class="close-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text style="height: 800px;">
          <div v-if="loadingLogs" class="text-center">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <p class="mt-2">Carregando logs...</p>
          </div>
          
          <div v-else-if="hasLogs" class="logs-container">
            <!-- Visualização lado-a-lado para CRONOGRAMA com RETRY -->
            <template v-if="isCronogramaWithRetry">
              <div class="d-flex logs-side-by-side">
                <!-- Logs do CRONOGRAMA (lado esquerdo) -->
                <div class="flex-1 mr-2">
                  <div class="log-section">
                    <div class="log-header">
                      <div class="d-flex align-center">
                        <div>
                          <h3 class="text-subtitle-1 font-weight-bold mb-0">
                            <v-icon class="mr-2" color="primary">mdi-clock</v-icon>
                            CRONOGRAMA
                          </h3>
                          <div class="caption text--secondary">
                            ID: {{ cronogramaId }} | HostID: {{ hostId }} | Host: {{ hostName }} | Sistema: {{ sistema }}
                          </div>
                        </div>
                        <v-spacer></v-spacer>
                        <div class="d-flex align-center">
                          <v-btn 
                            icon 
                            small
                            @click="toggleExpandCronograma"
                            class="mr-2"
                            title="Expandir/Recolher"
                          >
                            <v-icon small>{{ cronogramaExpanded ? 'mdi-arrow-collapse' : 'mdi-arrow-expand' }}</v-icon>
                          </v-btn>
                          <v-btn 
                            icon 
                            large
                            color="primary"
                            @click="analisarIA('cronograma')"
                            class="mr-2"
                            title="Analisar com IA"
                          >
                            <v-icon large>mdi-brain</v-icon>
                          </v-btn>
                          <v-btn 
                            icon 
                            large
                            :color="hasCronogramaVideo ? 'success' : 'grey'" 
                            @click="openVideoDialog('cronograma')"
                            title="Ver vídeo"
                            :disabled="!hasCronogramaVideo"
                          >
                            <v-icon large>mdi-video</v-icon>
                          </v-btn>
                          <v-btn 
                            icon 
                            large
                            color="warning"
                            @click="editarComentario('cronograma')"
                            class="ml-2"
                            title="Editar Comentário"
                          >
                            <v-icon large>mdi-comment-edit</v-icon>
                          </v-btn>
                          <v-chip x-small class="ml-2" :color="cronogramaStatus ? 'success' : 'error'">
                            {{ dataFormatada }}
                          </v-chip>
                        </div>
                      </div>
                    </div>
                    <pre :class="['log-content', {'log-content-expanded': cronogramaExpanded}]" v-html="cronogramaLogContent"></pre>
                  </div>
                </div>
                
                <!-- Logs do CRONOGRAMA_RETRY (lado direito) -->
                <div class="flex-1 ml-2">
                  <div class="log-section">
                    <div class="log-header">
                      <div class="d-flex align-center">
                        <div>
                          <h3 class="text-subtitle-1 font-weight-bold mb-0">
                            <v-icon class="mr-2" color="warning">mdi-refresh</v-icon>
                            CRONOGRAMA_RETRY
                          </h3>
                          <div class="caption text--secondary">
                            ID: {{ retryId }} | HostID: {{ hostId }} | Host: {{ hostName }} | Sistema: {{ sistema }}
                          </div>
                        </div>
                        <v-spacer></v-spacer>
                        <div class="d-flex align-center">
                          <v-btn 
                            icon 
                            small
                            @click="toggleExpandRetry"
                            class="mr-2"
                            title="Expandir/Recolher"
                          >
                            <v-icon small>{{ retryExpanded ? 'mdi-arrow-collapse' : 'mdi-arrow-expand' }}</v-icon>
                          </v-btn>
                          <v-btn 
                            icon 
                            large
                            color="primary"
                            @click="analisarIA('retry')"
                            class="mr-2"
                            title="Analisar com IA"
                          >
                            <v-icon large>mdi-brain</v-icon>
                          </v-btn>
                          <v-btn 
                            icon 
                            large
                            :color="hasRetryVideo ? 'success' : 'grey'" 
                            @click="openVideoDialog('retry')"
                            title="Ver vídeo"
                            :disabled="!hasRetryVideo"
                          >
                            <v-icon large>mdi-video</v-icon>
                          </v-btn>
                          <v-btn 
                            icon 
                            large
                            color="warning"
                            @click="editarComentario('retry')"
                            class="ml-2"
                            title="Editar Comentário"
                          >
                            <v-icon large>mdi-comment-edit</v-icon>
                          </v-btn>
                          <v-chip x-small class="ml-2" :color="retryStatus ? 'success' : 'error'">
                            {{ dataFormatada }}
                          </v-chip>
                        </div>
                      </div>
                    </div>
                    <pre :class="['log-content', {'log-content-expanded': retryExpanded}]" v-html="retryLogContent"></pre>
                  </div>
                </div>
              </div>
            </template>
            
            <!-- Visualização tradicional para outros métodos (MANUAL ou CRONOGRAMA sem RETRY) -->
            <template v-else>
              <div class="log-section">
                <div class="log-header">
                  <div class="d-flex align-center">
                    <div>
                      <h3 class="text-subtitle-1 font-weight-bold mb-0">
                        <v-icon class="mr-2" :color="getMetodoColor(metodoAtual)">{{ getMetodoIcon(metodoAtual) }}</v-icon>
                        {{ metodoAtual }}
                      </h3>
                      <div class="caption text--secondary">
                        ID: {{ atualizacaoId || cronogramaId }} | HostID: {{ hostId }} | Host: {{ hostName }} | Sistema: {{ sistema }}
                      </div>
                    </div>
                    <v-spacer></v-spacer>
                    <div class="d-flex align-center">
                      <v-btn 
                        icon 
                        large
                        color="primary"
                        @click="analisarIA()"
                        class="mr-2"
                        title="Analisar com IA"
                      >
                        <v-icon large>mdi-brain</v-icon>
                      </v-btn>
                      <v-btn 
                        icon 
                        large
                        :color="hasVideos ? 'success' : 'grey'" 
                        @click="openVideoDialog()"
                        title="Ver vídeo"
                        :disabled="!hasVideos"
                      >
                        <v-icon large>mdi-video</v-icon>
                      </v-btn>
                      <v-btn 
                        icon 
                        large
                        color="warning"
                        @click="editarComentario()"
                        class="ml-2"
                        title="Editar Comentário"
                      >
                        <v-icon large>mdi-comment-edit</v-icon>
                      </v-btn>
                      <v-chip small class="ml-2" :color="getStatusColor()">
                        {{ dataFormatada }}
                      </v-chip>
                    </div>
                  </div>
                </div>
                <pre class="log-content full-height" v-html="logContent"></pre>
              </div>
            </template>
          </div>
          
          <div v-else-if="!loadingLogs && !hasLogs">
            <v-alert type="info" outlined>
              <div class="d-flex align-center">
                <v-icon class="mr-2">mdi-information</v-icon>
                <div>
                  <strong>Nenhum log encontrado</strong>
                  <br>
                  <span class="text-body-2">
                    Não foram encontrados logs para {{ hostName }} - {{ sistema }} na data {{ dataFormatada }}
                  </span>
                </div>
              </div>
            </v-alert>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeDialog">
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog para visualizar vídeos -->
    <v-dialog v-model="videoDialog" max-width="1000px" persistent>
      <v-card>
        <v-card-title>
          <v-icon class="mr-2" color="red">mdi-video</v-icon>
          Gravação de Vídeo
          <v-spacer></v-spacer>
          <v-btn icon @click="videoDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text>
          <!-- Visualização de vídeo selecionado -->
          <div v-if="selectedVideo" class="text-center">
            <video 
              :src="selectedVideo.url" 
              controls
              autoplay
              style="max-width: 100%; height: auto; border-radius: 8px;"
              preload="metadata"
              @error="onVideoError"
            >
              Seu navegador não suporta a reprodução de vídeos.
            </video>
          </div>
          
          <!-- Mensagem quando não há vídeo selecionado -->
          <div v-else class="text-center py-8">
            <v-icon size="64" color="grey lighten-1">mdi-video-off</v-icon>
            <p class="mt-4 text-subtitle-1 grey--text">Carregando vídeo...</p>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="videoDialog = false">
            Fechar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import hostsService from '@/services/hostsService'
import dashboardService from '@/services/dashboardService'
import authService from '@/services/authService'
import axios from 'axios'
import { formatarLog } from '@/utils/formatterlog'
import { getApiUrl, getDefaultHeaders } from '@/config/env'

export default {
  name: 'DashboardLogViewer',
  
  props: {
    value: {
      type: Boolean,
      default: false
    },
    hostId: {
      type: String,
      required: true
    },
    hostName: {
      type: String,
      required: true
    },
    sistema: {
      type: String,
      required: true
    },
    dataFormatada: {
      type: String,
      required: true
    },
    atualizacaoId: {
      type: Number,
      default: null
    }
  },

  data() {
    return {
      // Logs
      loadingLogs: false,
      logContent: '',
      cronogramaLogContent: '',
      retryLogContent: '',
      atualizacaoStatus: false,
      cronogramaStatus: false,
      retryStatus: false,
      metodoAtual: 'MANUAL', // Método da atualização atual (MANUAL, CRONOGRAMA, etc.)
      
      // IDs de atualizações
      cronogramaId: null,
      retryId: null,
      
      // Vídeos relacionados
      videos: [],
      cronogramaVideos: [],
      retryVideos: [],
      selectedVideo: null,
      videoDialog: false,
      currentVideoType: null, // 'cronograma' ou 'retry'
      
      // Flag para evitar duplicação de requisições
      isLoadingLogs: false,
      
      // Controle de expansão dos logs
      cronogramaExpanded: false,
      retryExpanded: false
    }
  },

  computed: {
    internalDialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    
    hasLogs() {
      return !!this.cronogramaLogContent || !!this.retryLogContent
    },
    
    hasRetryLog() {
      return !!this.retryLogContent
    },

    hasVideos() {
      return this.videos.length > 0
    },
    
    hasCronogramaVideo() {
      return this.cronogramaVideos.length > 0
    },
    
    hasRetryVideo() {
      return this.retryVideos.length > 0
    },
    
    /**
     * Determina se deve usar a visualização unificada para CRONOGRAMA e CRONOGRAMA_RETRY
     * Só usamos essa visualização quando:
     * 1. Temos logs de CRONOGRAMA
     * 2. Temos logs de CRONOGRAMA_RETRY (ou seja, existe um retry)
     */
    isCronogramaWithRetry() {
      // Verificar se temos conteúdo de log de CRONOGRAMA e RETRY
      return !!this.cronogramaLogContent && !!this.retryLogContent;
    }
  },

  watch: {
    value: {
      handler(newVal) {
        if (newVal && this.hostId && this.sistema && this.dataFormatada && !this.isLoadingLogs) {
          this.loadLogs()
        }
        
        // Limpar URLs temporárias quando o diálogo for fechado
        if (!newVal) {
          this.cleanupTempUrl()
          this.selectedVideo = null
          this.videoDialog = false
        }
      },
      immediate: true
    },
    
    videoDialog(newVal) {
      if (!newVal) {
        // Limpar o vídeo selecionado quando fechar o diálogo de vídeo
        this.selectedVideo = null
      }
    }
  },

  beforeDestroy() {
    this.cleanupTempUrl()
  },

  methods: {
    // Funções auxiliares para ícones e cores dos métodos
    getMetodoIcon(metodo) {
      switch (metodo) {
        case 'CRONOGRAMA':
          return 'mdi-clock'
        case 'CRONOGRAMA_RETRY':
          return 'mdi-refresh'
        case 'MANUAL':
          return 'mdi-hand-pointing-up'
        default:
          return 'mdi-cog'
      }
    },
    
    getMetodoColor(metodo) {
      switch (metodo) {
        case 'CRONOGRAMA':
          return 'primary'
        case 'CRONOGRAMA_RETRY':
          return 'warning'
        case 'MANUAL':
          return '#82B982'
        default:
          return 'grey'
      }
    },

    async loadLogs() {
      if (!this.hostId || !this.sistema || !this.dataFormatada || this.isLoadingLogs) return
      
      this.isLoadingLogs = true
      this.loadingLogs = true
      
      // Limpar vídeos anteriores
      this.videos = []
      this.cronogramaVideos = []
      this.retryVideos = []
      this.selectedVideo = null
      
      try {
        console.log(`Carregando logs - HostID: ${this.hostId}, Sistema: ${this.sistema}, Data: ${this.dataFormatada}, AtualizacaoID: ${this.atualizacaoId}`)
        
          // Extrair data do dataFormatada para buscar atualizações nesta data
          const data = this.extrairData(this.dataFormatada)
        if (!data) {
          throw new Error('Formato de data inválido')
        }
        
        // Buscar todas as atualizações do host nesta data
            const filtros = {
              hostid: this.hostId,
              sistema: this.sistema,
              dataInicio: `${data}T00:00:00`,
              dataFim: `${data}T23:59:59`
            }
            
        // Se temos um ID específico, vamos verificar se ele corresponde a uma atualização válida
        let atualizacaoEspecifica = null
        if (this.atualizacaoId) {
          try {
            const respAtualizacao = await dashboardService.getLogAtualizacao(this.atualizacaoId)
            if (respAtualizacao && (respAtualizacao.controle || respAtualizacao.logs)) {
              console.log(`Encontrada atualização específica com ID ${this.atualizacaoId}`)
              atualizacaoEspecifica = {
                id: this.atualizacaoId,
                // Tentar obter o método da resposta
                metodo: respAtualizacao.controle ? respAtualizacao.controle.metodo : null
              }
            }
          } catch (error) {
            console.warn(`Erro ao buscar atualização específica com ID ${this.atualizacaoId}:`, error)
          }
            }
            
            const respAtualizacoes = await dashboardService.getAtualizacoes(filtros)
        if (!respAtualizacoes.success || !respAtualizacoes.atualizacoes.length) {
          throw new Error('Nenhuma atualização encontrada para esta data')
        }
        
        console.log(`Atualizações encontradas: ${respAtualizacoes.atualizacoes.length}`, respAtualizacoes.atualizacoes.map(a => `${a.id} (${a.metodo})`).join(', '))
        
        // Se temos uma atualização específica, vamos encontrá-la na lista
        let cronogramaAtualizacao = null;
        let retryAtualizacao = null;
        
        if (atualizacaoEspecifica) {
          // Encontrar a atualização específica na lista
          const atualizacao = respAtualizacoes.atualizacoes.find(a => a.id === atualizacaoEspecifica.id);
          
          if (atualizacao) {
            console.log(`Atualização específica encontrada: ${atualizacao.id} (${atualizacao.metodo})`);
            
            // Se a atualização específica é um CRONOGRAMA, mostrar ela e seu RETRY (se existir)
            if (atualizacao.metodo === 'CRONOGRAMA') {
              cronogramaAtualizacao = atualizacao;
              
              // Buscar o RETRY correspondente (mesmo host, sistema e data)
              const dataFormatada = this.extrairDataSemHora(atualizacao.started_at);
              retryAtualizacao = respAtualizacoes.atualizacoes.find(a => 
                a.metodo === 'CRONOGRAMA_RETRY' && 
                a.hostid === atualizacao.hostid && 
                a.sistema === atualizacao.sistema && 
                this.extrairDataSemHora(a.started_at) === dataFormatada
              );
              
              if (retryAtualizacao) {
                console.log(`RETRY correspondente encontrado: ${retryAtualizacao.id}`);
              }
            } 
            // Se a atualização específica é um RETRY, mostrar ele e seu CRONOGRAMA original (se existir)
            else if (atualizacao.metodo === 'CRONOGRAMA_RETRY') {
              retryAtualizacao = atualizacao;
              
              // Buscar o CRONOGRAMA original correspondente
              const dataFormatada = this.extrairDataSemHora(atualizacao.started_at);
              cronogramaAtualizacao = respAtualizacoes.atualizacoes.find(a => 
                a.metodo === 'CRONOGRAMA' && 
                a.hostid === atualizacao.hostid && 
                a.sistema === atualizacao.sistema && 
                this.extrairDataSemHora(a.started_at) === dataFormatada
              );
              
              if (cronogramaAtualizacao) {
                console.log(`CRONOGRAMA original encontrado: ${cronogramaAtualizacao.id}`);
              }
            }
            // Se não é nem CRONOGRAMA nem RETRY, mostrar apenas ela
            else {
              // Para outros métodos, apenas mostrar a atualização específica
              console.log(`Atualização de outro método: ${atualizacao.metodo}`);
              cronogramaAtualizacao = atualizacao;
            }
          } else {
            console.warn(`Atualização específica com ID ${atualizacaoEspecifica.id} não encontrada na lista de atualizações`);
          }
        } else {
          // Se não temos uma atualização específica, usar o comportamento padrão
          console.log("Sem atualização específica, usando comportamento padrão");
          cronogramaAtualizacao = respAtualizacoes.atualizacoes.find(a => a.metodo === 'CRONOGRAMA');
          retryAtualizacao = respAtualizacoes.atualizacoes.find(a => a.metodo === 'CRONOGRAMA_RETRY');
        }
        
        // Carregar logs do CRONOGRAMA (principal)
        if (cronogramaAtualizacao) {
          this.cronogramaId = cronogramaAtualizacao.id
          this.cronogramaStatus = cronogramaAtualizacao.atualizado
          this.metodoAtual = cronogramaAtualizacao.metodo
          
          console.log('[DEBUG] Carregando logs do CRONOGRAMA:', {
            id: cronogramaAtualizacao.id,
            metodo: cronogramaAtualizacao.metodo,
            atualizado: cronogramaAtualizacao.atualizado
          });
          
          // Buscar logs da tabela controle_atualizacoes
          const response = await dashboardService.getLogAtualizacao(cronogramaAtualizacao.id)
          
          console.log('[DEBUG] Resposta da API para CRONOGRAMA:', {
            success: response?.success,
            hasControle: !!response?.controle,
            hasLogs: !!response?.logs,
            controleLogs: response?.controle?.logs ? 'presente' : 'ausente',
            logs: response?.logs ? 'presente' : 'ausente'
          });
          
          if (response && (response.controle || response.logs)) {
            // Extrair logs
            let logText = '';
            if (response.controle && response.controle.logs) {
              logText = response.controle.logs
              console.log('[DEBUG] Logs extraídos de response.controle.logs, tamanho:', logText.length);
            } else if (response.logs) {
              logText = response.logs
              console.log('[DEBUG] Logs extraídos de response.logs, tamanho:', logText.length);
            }
            
            // Ajustar timestamps do log (subtrair 3 horas)
            this.cronogramaLogContent = this.ajustarTimestampsLog(logText)
            console.log('[DEBUG] cronogramaLogContent definido, tamanho:', this.cronogramaLogContent.length);
            
            // Buscar vídeos relacionados ao CRONOGRAMA
            await this.buscarVideosEspecificos(cronogramaAtualizacao.id, 'cronograma')
          } else {
            console.warn('[DEBUG] Resposta da API não contém logs válidos para CRONOGRAMA:', response);
            this.cronogramaLogContent = ''
          }
        }
        
        // Carregar logs do CRONOGRAMA_RETRY (se disponível)
        if (retryAtualizacao) {
          this.retryId = retryAtualizacao.id
          this.retryStatus = retryAtualizacao.atualizado
          
          console.log('[DEBUG] Carregando logs do RETRY:', {
            id: retryAtualizacao.id,
            metodo: retryAtualizacao.metodo,
            atualizado: retryAtualizacao.atualizado
          });
          
          // Buscar logs da tabela controle_atualizacoes
          const response = await dashboardService.getLogAtualizacao(retryAtualizacao.id)
          
          console.log('[DEBUG] Resposta da API para RETRY:', {
            success: response?.success,
            hasControle: !!response?.controle,
            hasLogs: !!response?.logs,
            controleLogs: response?.controle?.logs ? 'presente' : 'ausente',
            logs: response?.logs ? 'presente' : 'ausente'
          });
          
          if (response && (response.controle || response.logs)) {
            // Extrair logs
            let logText = '';
            if (response.controle && response.controle.logs) {
              logText = response.controle.logs
              console.log('[DEBUG] Logs extraídos de response.controle.logs (RETRY), tamanho:', logText.length);
            } else if (response.logs) {
              logText = response.logs
              console.log('[DEBUG] Logs extraídos de response.logs (RETRY), tamanho:', logText.length);
            }
            
            // Ajustar timestamps do log (subtrair 3 horas)
            this.retryLogContent = this.ajustarTimestampsLog(logText)
            console.log('[DEBUG] retryLogContent definido, tamanho:', this.retryLogContent.length);
            
            // Buscar vídeos relacionados ao RETRY
            await this.buscarVideosEspecificos(retryAtualizacao.id, 'retry')
          } else {
            console.warn('[DEBUG] Resposta da API não contém logs válidos para RETRY:', response);
            this.retryLogContent = ''
          }
        } else {
          this.retryLogContent = ''
        }
        
        // Para compatibilidade com o código existente
        this.logContent = this.cronogramaLogContent
        this.atualizacaoStatus = this.cronogramaStatus
        this.videos = [...this.cronogramaVideos, ...this.retryVideos]
        
        // Verificar se realmente temos logs antes de lançar erro
        console.log('[DEBUG] Verificação de logs:', {
          cronogramaLogContent: this.cronogramaLogContent,
          retryLogContent: this.retryLogContent,
          hasLogs: this.hasLogs,
          cronogramaId: this.cronogramaId,
          retryId: this.retryId
        });
        
        // Só lançar erro se realmente não temos nenhum log
        if (!this.cronogramaLogContent && !this.retryLogContent) {
          console.warn('[DEBUG] Nenhum log encontrado - Verificando se é um problema de carregamento');
          
          // Tentar carregar logs diretamente se temos um ID específico
          if (this.atualizacaoId) {
            try {
              console.log('[DEBUG] Tentando carregar logs diretamente para ID:', this.atualizacaoId);
              const response = await dashboardService.getLogAtualizacao(this.atualizacaoId);
              
              if (response && response.success && response.controle && response.controle.logs) {
                console.log('[DEBUG] Logs encontrados diretamente:', response.controle.logs.substring(0, 100) + '...');
                
                // Definir o log diretamente
                if (response.controle.metodo === 'CRONOGRAMA_RETRY') {
                  this.retryLogContent = this.ajustarTimestampsLog(response.controle.logs);
                  this.retryId = this.atualizacaoId;
                  this.retryStatus = response.controle.atualizado;
                } else {
                  this.cronogramaLogContent = this.ajustarTimestampsLog(response.controle.logs);
                  this.cronogramaId = this.atualizacaoId;
                  this.cronogramaStatus = response.controle.atualizado;
                  this.metodoAtual = response.controle.metodo;
                }
                
                // Atualizar logContent para compatibilidade
                this.logContent = this.cronogramaLogContent || this.retryLogContent;
                
                console.log('[DEBUG] Logs carregados com sucesso após tentativa direta');
              } else {
                console.warn('[DEBUG] Resposta da API não contém logs válidos:', response);
                throw new Error('Nenhum log disponível para esta atualização');
              }
            } catch (directError) {
              console.error('[DEBUG] Erro ao carregar logs diretamente:', directError);
              throw new Error('Nenhum log disponível para esta atualização');
            }
          } else {
            throw new Error('Nenhum log disponível para esta atualização');
          }
        }
      } catch (error) {
        console.error('Erro ao buscar logs:', error)
        this.$emit('show-snackbar', error.message || 'Erro ao buscar logs', 'error')
        this.cronogramaLogContent = ''
        this.retryLogContent = ''
        this.logContent = ''
        this.videos = []
        this.cronogramaVideos = []
        this.retryVideos = []
      } finally {
        this.loadingLogs = false
        this.isLoadingLogs = false
      }
    },
    
    async buscarVideosPorId(atualizacaoId) {
      try {
        console.log(`Buscando vídeos para ID de atualização: ${atualizacaoId}`)
        
        // Buscar todos os logs do host
        const response = await hostsService.getLogsByHostId(this.hostId)
        
        if (response && response.success && response.logs && response.logs.sistemas) {
          // Inicializar array de vídeos
          const videosEncontrados = []
          
          // Expressão regular para encontrar o ID no nome do arquivo
          const regexId = new RegExp(`${atualizacaoId}|gravação-${atualizacaoId}|gravacao-${atualizacaoId}`)
          
          // Procurar em todos os sistemas e datas
          response.logs.sistemas.forEach(sistemaLog => {
            // Para logs normais de sistema
            if (sistemaLog.datas) {
              sistemaLog.datas.forEach(dataLog => {
                if (dataLog.arquivos) {
                  // Filtrar apenas arquivos MP4 que contêm o ID da atualização
                  const videos = dataLog.arquivos.filter(
                    arquivo => arquivo.nome.endsWith('.mp4') && regexId.test(arquivo.nome)
                  )
                  
                  // Adicionar informações extras para facilitar a visualização depois
                  videos.forEach(video => {
                    video.sistema = sistemaLog.sistema
                    video.data = dataLog.data
                  })
                  
                  videosEncontrados.push(...videos)
                }
              })
            }
            
            // Para logs de AttManual que têm subSistemas
            if (sistemaLog.subSistemas) {
              sistemaLog.subSistemas.forEach(subSistema => {
                if (subSistema.datas) {
                  subSistema.datas.forEach(dataLog => {
                    if (dataLog.arquivos) {
                      // Filtrar apenas arquivos MP4 que contêm o ID da atualização
                      const videos = dataLog.arquivos.filter(
                        arquivo => arquivo.nome.endsWith('.mp4') && regexId.test(arquivo.nome)
                      )
                      
                      // Adicionar informações extras
                      videos.forEach(video => {
                        video.sistema = `AttManual/${subSistema.sistema}`
                        video.data = dataLog.data
                      })
                      
                      videosEncontrados.push(...videos)
                    }
                  })
                }
              })
            }
          })
          
          console.log(`Encontrados ${videosEncontrados.length} vídeos para o ID ${atualizacaoId}`)
          
          // Atualizar o array de vídeos
          this.videos = videosEncontrados
          
          // Se encontrou vídeos, abrir automaticamente o diálogo de vídeo
          if (videosEncontrados.length > 0) {
            console.log(`Abrindo diálogo de vídeo automaticamente para ID ${atualizacaoId}`)
            this.videoDialog = true
            
            // Se houver apenas um vídeo, já selecioná-lo automaticamente
            if (videosEncontrados.length === 1) {
              console.log(`Reproduzindo automaticamente o vídeo: ${videosEncontrados[0].nome}`)
              this.viewVideo(videosEncontrados[0])
            }
          }
        }
      } catch (error) {
        console.error(`Erro ao buscar vídeos para ID ${atualizacaoId}:`, error)
        this.videos = []
      }
    },
    
    async buscarVideosEspecificos(atualizacaoId, tipo) {
      try {
        // Buscar todos os logs do host
        const response = await hostsService.getLogsByHostId(this.hostId)
        
        if (response && response.success && response.logs && response.logs.sistemas) {
          // Inicializar array de vídeos para o tipo específico
          const videos = []
          
          // Expressão regular para encontrar o ID no nome do arquivo
          const regexId = new RegExp(`-${atualizacaoId}(-|\\.|$)`)
          
          // Procurar em todos os sistemas e datas
          response.logs.sistemas.forEach(sistemaLog => {
            // Para logs normais de sistema
            if (sistemaLog.datas) {
              sistemaLog.datas.forEach(dataLog => {
                if (dataLog.arquivos) {
                  // Filtrar apenas arquivos MP4 que contêm o ID da atualização
                  const videosEncontrados = dataLog.arquivos.filter(
                    arquivo => arquivo.nome.endsWith('.mp4') && regexId.test(arquivo.nome)
                  )
                  
                  // Adicionar informações extras para facilitar a visualização depois
                  videosEncontrados.forEach(video => {
                    video.sistema = sistemaLog.sistema
                    video.data = dataLog.data
                    video.tipo = tipo // Adicionar o tipo (cronograma ou retry)
                  })
                  
                  videos.push(...videosEncontrados)
                }
              })
            }
            
            // Para logs de AttManual que têm subSistemas
            if (sistemaLog.subSistemas) {
              sistemaLog.subSistemas.forEach(subSistema => {
                if (subSistema.datas) {
                  subSistema.datas.forEach(dataLog => {
                    if (dataLog.arquivos) {
                      // Filtrar apenas arquivos MP4 que contêm o ID da atualização
                      const videosEncontrados = dataLog.arquivos.filter(
                        arquivo => arquivo.nome.endsWith('.mp4') && regexId.test(arquivo.nome)
                      )
                      
                      // Adicionar informações extras
                      videosEncontrados.forEach(video => {
                        video.sistema = `AttManual/${subSistema.sistema}`
                        video.data = dataLog.data
                        video.tipo = tipo // Adicionar o tipo (cronograma ou retry)
                      })
                      
                      videos.push(...videosEncontrados)
                    }
                  })
                }
              })
            }
          })
          
          // Atualizar o array de vídeos correspondente
          if (tipo === 'cronograma') {
            this.cronogramaVideos = videos
          } else if (tipo === 'retry') {
            this.retryVideos = videos
          }
        }
      } catch (error) {
        console.error(`Erro ao buscar vídeos de ${tipo}:`, error)
        if (tipo === 'cronograma') {
          this.cronogramaVideos = []
        } else if (tipo === 'retry') {
          this.retryVideos = []
        }
      }
    },
    
    async buscarVideos(atualizacaoId) {
      // Método mantido para compatibilidade
      try {
        // Buscar todos os logs do host
        const response = await hostsService.getLogsByHostId(this.hostId)
        
        if (response && response.success && response.logs && response.logs.sistemas) {
          // Inicializar array de vídeos
          this.videos = []
          
          // Expressão regular para encontrar o ID no nome do arquivo
          const regexId = new RegExp(`-${atualizacaoId}(-|\\.|$)`)
          
          // Procurar em todos os sistemas e datas
          response.logs.sistemas.forEach(sistemaLog => {
            // Para logs normais de sistema
            if (sistemaLog.datas) {
              sistemaLog.datas.forEach(dataLog => {
                if (dataLog.arquivos) {
                  // Filtrar apenas arquivos MP4 que contêm o ID da atualização
                  const videosEncontrados = dataLog.arquivos.filter(
                    arquivo => arquivo.nome.endsWith('.mp4') && regexId.test(arquivo.nome)
                  )
                  
                  // Adicionar informações extras para facilitar a visualização depois
                  videosEncontrados.forEach(video => {
                    video.sistema = sistemaLog.sistema
                    video.data = dataLog.data
                  })
                  
                  this.videos.push(...videosEncontrados)
                }
              })
            }
            
            // Para logs de AttManual que têm subSistemas
            if (sistemaLog.subSistemas) {
              sistemaLog.subSistemas.forEach(subSistema => {
                if (subSistema.datas) {
                  subSistema.datas.forEach(dataLog => {
                    if (dataLog.arquivos) {
                      // Filtrar apenas arquivos MP4 que contêm o ID da atualização
                      const videosEncontrados = dataLog.arquivos.filter(
                        arquivo => arquivo.nome.endsWith('.mp4') && regexId.test(arquivo.nome)
                      )
                      
                      // Adicionar informações extras
                      videosEncontrados.forEach(video => {
                        video.sistema = `AttManual/${subSistema.sistema}`
                        video.data = dataLog.data
                      })
                      
                      this.videos.push(...videosEncontrados)
                    }
                  })
                }
              })
            }
          })
        }
      } catch (error) {
        console.error('Erro ao buscar vídeos:', error)
        this.videos = []
      }
    },
    
    async openVideoDialog(tipo = null) {
      this.currentVideoType = tipo
      
      let videosParaMostrar = [];
      let atualizacaoId = null;
      
      if (tipo === 'cronograma') {
        videosParaMostrar = this.cronogramaVideos;
        atualizacaoId = this.cronogramaId;
      } else if (tipo === 'retry') {
        videosParaMostrar = this.retryVideos;
        atualizacaoId = this.retryId;
      } else {
        // Se não especificado, mostrar todos os vídeos (compatibilidade)
        videosParaMostrar = this.videos;
        atualizacaoId = this.atualizacaoId || this.cronogramaId;
      }
      
      // Se não temos vídeos ainda, mas temos um ID de atualização, buscar vídeos específicos para esse ID
      if (videosParaMostrar.length === 0 && atualizacaoId) {
        console.log(`Buscando vídeos específicos para o ID ${atualizacaoId}`);
        await this.buscarVideosPorId(atualizacaoId);
        
        // Atualizar a lista de vídeos após a busca
        if (tipo === 'cronograma') {
          videosParaMostrar = this.cronogramaVideos;
        } else if (tipo === 'retry') {
          videosParaMostrar = this.retryVideos;
        } else {
          videosParaMostrar = this.videos;
        }
      }
      
      if (videosParaMostrar.length > 0) {
        this.videoDialog = true;
        
        // Reproduzir o vídeo diretamente, mesmo se houver mais de um
        // Se houver múltiplos vídeos, escolher o primeiro
        this.viewVideo(videosParaMostrar[0]);
      } else {
        this.$emit('show-snackbar', 'Nenhum vídeo encontrado para esta atualização', 'warning');
      }
    },
    
    toggleExpandCronograma() {
      this.cronogramaExpanded = !this.cronogramaExpanded;
    },
    
    toggleExpandRetry() {
      this.retryExpanded = !this.retryExpanded;
    },
    
    analisarIA(tipo) {
      let atualizacaoId = null;
      
      if (tipo === 'cronograma') {
        atualizacaoId = this.cronogramaId
      } else if (tipo === 'retry') {
        atualizacaoId = this.retryId
      } else {
        // Se não for especificado um tipo, usar o ID da atualização principal
        atualizacaoId = this.atualizacaoId || this.cronogramaId
      }
      
      if (atualizacaoId) {
        // Emitir evento para o componente pai abrir o analisador de IA
        this.$emit('analisar-ia', {
          id: atualizacaoId,
          hostId: this.hostId,
          hostName: this.hostName,
          sistema: this.sistema,
          dataFormatada: this.dataFormatada
        })
      } else {
        this.$emit('show-snackbar', 'Não foi possível identificar a atualização para análise', 'error')
      }
    },

    editarComentario(tipo) {
      let atualizacaoId = null;
      let atualizacao = null;
      let atualizacaoRetry = null;
      
      if (tipo === 'cronograma') {
        atualizacaoId = this.cronogramaId
        atualizacao = {
          id: this.cronogramaId,
          hostid: this.hostId,
          hostName: this.hostName,
          sistema: this.sistema,
          metodo: 'CRONOGRAMA'
        }
        
        // Se existe retry relacionado, passar também
        if (this.retryId) {
          atualizacaoRetry = {
            id: this.retryId,
            hostid: this.hostId,
            hostName: this.hostName,
            sistema: this.sistema,
            metodo: 'CRONOGRAMA_RETRY'
          }
        }
      } else if (tipo === 'retry') {
        atualizacaoId = this.retryId
        atualizacao = {
          id: this.retryId,
          hostid: this.hostId,
          hostName: this.hostName,
          sistema: this.sistema,
          metodo: 'CRONOGRAMA_RETRY'
        }
        
        // Se existe cronograma relacionado, passar também
        if (this.cronogramaId) {
          atualizacaoRetry = null; // Retry não precisa passar o cronograma de volta
        }
      } else {
        // Se não for especificado um tipo, usar o ID da atualização principal
        atualizacaoId = this.atualizacaoId || this.cronogramaId
        atualizacao = {
          id: atualizacaoId,
          hostid: this.hostId,
          hostName: this.hostName,
          sistema: this.sistema,
          metodo: this.metodoAtual
        }
        
        // Se estamos no modo de visualização lado-a-lado e temos retry, passar também
        if (this.isCronogramaWithRetry && this.retryId) {
          atualizacaoRetry = {
            id: this.retryId,
            hostid: this.hostId,
            hostName: this.hostName,
            sistema: this.sistema,
            metodo: 'CRONOGRAMA_RETRY'
          }
        }
      }
      
      if (atualizacaoId && atualizacao) {
        // Emitir evento para o componente pai abrir o modal de comentário
        // Passar atualizacaoRetry apenas se existir
        const eventData = {
          ...atualizacao,
          modo: 'editar'
        };
        
        if (atualizacaoRetry) {
          eventData.atualizacaoRetry = atualizacaoRetry;
        }
        
        console.log('[DEBUG] editarComentario - Emitindo evento com:', eventData);
        
        this.$emit('editar-comentario', eventData)
      } else {
        this.$emit('show-snackbar', 'Não foi possível identificar a atualização para editar comentário', 'error')
      }
    },
    
    async viewVideo(video) {
      // Verificar se o usuário está autenticado
      if (!authService.isAuthenticated()) {
        this.$emit('show-snackbar', 'Você não está autenticado. Faça login novamente.', 'error')
        return
      }
      
      try {
        const token = localStorage.getItem('authToken')
        
        if (!token) {
          this.$emit('show-snackbar', 'Token de autenticação não encontrado. Faça login novamente.', 'error')
          return
        }
        
        const encodedSistema = encodeURIComponent(video.sistema)
        const encodedData = encodeURIComponent(video.data)
        const encodedFileName = encodeURIComponent(video.nome)
        
        const fullUrl = getApiUrl(`/hosts/${this.hostId}/logs/${encodedSistema}/${encodedData}/${encodedFileName}`)
        
        const requestConfig = {
          headers: {
            ...getDefaultHeaders(),
            'Authorization': `Bearer ${token}`,
            'Accept': 'video/mp4'
          },
          responseType: 'blob',
          timeout: 30000
        }
        
        const response = await axios.get(fullUrl, requestConfig)
        
        if (response.status === 200) {
          const blob = new Blob([response.data], { type: 'video/mp4' })
          const url = URL.createObjectURL(blob)
          
          this.selectedVideo = {
            nome: video.nome,
            url: url,
            blob: blob
          }
        }
      } catch (error) {
        console.error('Erro ao visualizar vídeo:', error)
        this.$emit('show-snackbar', 'Erro ao carregar vídeo: ' + error.message, 'error')
      }
    },

    closeDialog() {
      this.internalDialog = false
      this.selectedVideo = null
      this.videoDialog = false
    },

    cleanupTempUrl() {
      if (this.selectedVideo && this.selectedVideo.url && this.selectedVideo.blob) {
        URL.revokeObjectURL(this.selectedVideo.url)
      }
    },

    getStatusColor() {
      return this.atualizacaoStatus ? 'success' : 'error'
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return 'Data inválida'
      }
    },

    onVideoError(event) {
      console.error('Erro no vídeo:', event)
      this.$emit('show-snackbar', 'Erro ao carregar vídeo', 'error')
    },
    
    /**
     * Gera e copia para a área de transferência um link compartilhável para este log
     * incluindo filtros necessários para encontrar a atualização
     */
    copiarLinkCompartilhavel() {
      try {
        // Determinar qual ID de atualização usar
        let atualizacaoId = null
        
        // Se estamos visualizando um CRONOGRAMA com RETRY, usar o ID do CRONOGRAMA por padrão
        if (this.isCronogramaWithRetry) {
          atualizacaoId = this.cronogramaId
        } else {
          // Caso contrário, usar o ID da atualização que está sendo mostrada
          atualizacaoId = this.atualizacaoId || this.cronogramaId
        }
        
        if (!atualizacaoId) {
          this.$emit('show-snackbar', 'Não foi possível gerar o link compartilhável', 'error')
          return
        }
        
        // Extrair informações para criar os filtros
        const dataFormatada = this.dataFormatada // Formato DD.MM.YYYY
        const hostId = this.hostId
        const sistema = this.sistema
        
        // Converter dataFormatada (DD.MM.YYYY) para YYYY-MM-DD para os filtros
        let dataFiltro = null
        if (dataFormatada) {
          try {
            const partes = dataFormatada.split('.')
            if (partes.length === 3) {
              dataFiltro = `${partes[2]}-${partes[1]}-${partes[0]}`
            }
          } catch (error) {
            console.error('Erro ao converter data formatada:', error)
          }
        }
        
        // Criar objeto de filtros que será enviado como parte da URL
        const filtros = {
          dataInicio: dataFiltro,
          dataFim: dataFiltro,
          hostid: hostId,
          sistema: sistema
        }
        
        // Serializar filtros para URL
        const filtrosSerializados = encodeURIComponent(JSON.stringify(filtros))
        
        // Construir a URL base (sem o hash)
        const baseUrl = window.location.origin + window.location.pathname
        
        // Construir a URL completa com o hash, o parâmetro logview e os filtros
        const shareableUrl = `${baseUrl}#/dashboard?logview=${atualizacaoId}&filtros=${filtrosSerializados}`
        
        console.log('🔗 Link compartilhável gerado:', shareableUrl)
        
        // Copiar para a área de transferência
        navigator.clipboard.writeText(shareableUrl)
          .then(() => {
            this.$emit('show-snackbar', 'Link compartilhável copiado para a área de transferência!', 'success')
          })
          .catch(err => {
            console.error('Erro ao copiar para a área de transferência:', err)
            // Fallback para método alternativo em caso de erro
            this.copiarTextoFallback(shareableUrl)
          })
      } catch (error) {
        console.error('Erro ao gerar link compartilhável:', error)
        this.$emit('show-snackbar', 'Erro ao gerar link compartilhável', 'error')
      }
    },
    
    /**
     * Método alternativo para copiar texto para a área de transferência
     * quando a API Clipboard não está disponível
     */
    copiarTextoFallback(texto) {
      try {
        // Criar elemento temporário
        const el = document.createElement('textarea')
        el.value = texto
        el.setAttribute('readonly', '')
        el.style.position = 'absolute'
        el.style.left = '-9999px'
        document.body.appendChild(el)
        
        // Selecionar e copiar
        const selecionado = document.getSelection().rangeCount > 0 
          ? document.getSelection().getRangeAt(0) 
          : false
        
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
        
        // Restaurar seleção original se existia
        if (selecionado) {
          document.getSelection().removeAllRanges()
          document.getSelection().addRange(selecionado)
        }
        
        this.$emit('show-snackbar', 'Link compartilhável copiado para a área de transferência!', 'success')
      } catch (err) {
        console.error('Erro no fallback de cópia:', err)
        this.$emit('show-snackbar', 'Não foi possível copiar o link. Por favor, copie manualmente da URL.', 'warning')
      }
    },
    
    ajustarTimestampsLog(texto) {
      // Usar o módulo centralizado para formatação
      return formatarLog(texto, true); // true para debug
    },
    
    extrairData(dataFormatada) {
      // Converter formato DD.MM.YYYY para YYYY-MM-DD
      if (!dataFormatada) return null
      
      try {
        // Verificar o formato (se for DD.MM.YYYY)
        if (dataFormatada.includes('.')) {
          const partes = dataFormatada.split('.')
          if (partes.length === 3) {
            return `${partes[2]}-${partes[1]}-${partes[0]}`
          }
        }
        
        // Se não estiver no formato específico, tentar converter diretamente
        const data = new Date(dataFormatada)
        const ano = data.getFullYear()
        const mes = String(data.getMonth() + 1).padStart(2, '0')
        const dia = String(data.getDate()).padStart(2, '0')
        return `${ano}-${mes}-${dia}`
      } catch (error) {
        console.error('Erro ao extrair data:', error)
        return null
      }
    },
    
    /**
     * Extrai apenas a data (sem a hora) de uma string de data ISO
     * @param {string} dataString - String de data no formato ISO ou similar
     * @returns {string} Data no formato YYYY-MM-DD
     */
    extrairDataSemHora(dataString) {
      if (!dataString) return '';
      
      // Extrair a parte da data (YYYY-MM-DD)
      try {
        const data = new Date(dataString);
        const ano = data.getFullYear();
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const dia = String(data.getDate()).padStart(2, '0');
        return `${ano}-${mes}-${dia}`;
      } catch (error) {
        console.error('Erro ao extrair data:', error);
        return '';
      }
    }
  }
}
</script>

<style>
.cursor-pointer {
  cursor: pointer;
}

.logs-container {
  display: flex;
  flex-direction: column;
  height: calc(100% - 20px);
  overflow-y: auto;
}

.log-section {
  margin-bottom: 24px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%);
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.log-section:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.log-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 12px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  font-weight: 600;
}

.log-content {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  padding: 20px;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-x: auto;
  overflow-y: auto;
  font-family: 'Fira Code', 'Courier New', Courier, monospace;
  font-size: 13px;
  line-height: 1.5;
  max-height: 400px;
  transition: max-height 0.3s ease;
  color: #2c3e50;
  border-radius: 0 0 12px 12px;
}

.log-content-expanded {
  max-height: 800px;
}

.full-height {
  height: calc(100% - 20px);
  max-height: none;
}

.v-list-item {
  border-radius: 8px;
  margin-bottom: 6px;
  transition: all 0.3s ease;
}

.v-list-item:hover {
  background-color: rgba(0, 0, 0, 0.05);
  transform: translateX(4px);
}

.v-btn--active {
  background-color: rgba(25, 118, 210, 0.1);
}

/* Tema escuro */
.theme-dark .log-section {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(45, 45, 45, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.theme-dark .log-section:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
}

.theme-dark .log-header {
  background: linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

.theme-dark .log-content {
  background: linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%);
  color: #e0e0e0;
  border-radius: 0 0 12px 12px;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-x: auto;
  overflow-y: auto;
}

.theme-dark .v-list-item:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.theme-dark .v-btn--active {
  background-color: rgba(100, 181, 246, 0.2);
}

/* Melhorias no scrollbar */
.log-content::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.log-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

.log-content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
  transition: background 0.3s ease;
}

.log-content::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.5);
}

.log-content::-webkit-scrollbar-corner {
  background: rgba(0, 0, 0, 0.1);
}

.theme-dark .log-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
}

.theme-dark .log-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
}

.theme-dark .log-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}

.theme-dark .log-content::-webkit-scrollbar-corner {
  background: rgba(255, 255, 255, 0.1);
}

/* Animações suaves */
.log-section {
  animation: fadeInUp 0.4s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Layout lado-a-lado para logs */
.logs-side-by-side {
  gap: 16px;
  height: 100%;
}

.logs-side-by-side .flex-1 {
  flex: 1 1 0%;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.logs-side-by-side .log-section {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.logs-side-by-side .log-content {
  flex: 1;
  height: 100%;
  max-height: none;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-x: auto;
  overflow-y: auto;
}

/* Responsividade para layout lado-a-lado */
@media (max-width: 1200px) {
  .logs-side-by-side {
    flex-direction: column;
  }
  
  .logs-side-by-side .flex-1 {
    margin-right: 0 !important;
    margin-left: 0 !important;
    margin-bottom: 16px;
  }
  
  .logs-side-by-side .flex-1:last-child {
    margin-bottom: 0;
  }
}

/* Responsividade */
@media (max-width: 768px) {
  .log-content {
    font-size: 11px;
    padding: 16px;
  }
  
  .log-header {
    padding: 10px 16px;
  }
  
  .log-section {
    margin-bottom: 16px;
  }
  
  .logs-side-by-side {
    gap: 12px;
  }
}

/* Melhorias na tipografia dos logs */
.log-content {
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.theme-dark .log-content {
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

/* Destaque para timestamps nos logs */
.log-content {
  position: relative;
}

/* Destaque para timestamps - maior especificidade */
.log-content .timestamp-highlight,
pre .timestamp-highlight,
.timestamp-highlight {
  color: #229A00 !important;
  font-weight: 600 !important;
  font-family: Consolas, monospace !important;
  background-color: rgba(34, 154, 0, 0.1) !important;
  padding: 2px 4px !important;
  border-radius: 3px !important;
  border: 1px solid rgba(34, 154, 0, 0.3) !important;
  transition: all 0.2s ease !important;
  display: inline-block !important;
  text-shadow: none !important;
}

.log-content .timestamp-highlight:hover,
pre .timestamp-highlight:hover,
.timestamp-highlight:hover {
  background-color: rgba(34, 154, 0, 0.2) !important;
  border-color: rgba(34, 154, 0, 0.5) !important;
  transform: scale(1.02) !important;
}

/* Tema escuro para timestamps - maior especificidade */
.theme-dark .log-content .timestamp-highlight,
.theme-dark pre .timestamp-highlight,
.theme-dark .timestamp-highlight,
.v-application.theme--dark .log-content .timestamp-highlight,
.v-application.theme--dark pre .timestamp-highlight,
.v-application.theme--dark .timestamp-highlight {
  color: #4CAF50 !important;
  background-color: rgba(76, 175, 80, 0.15) !important;
  border-color: rgba(76, 175, 80, 0.4) !important;
  font-weight: 600 !important;
  font-family: Consolas, monospace !important;
  padding: 2px 4px !important;
  border-radius: 3px !important;
  border: 1px solid rgba(76, 175, 80, 0.4) !important;
  transition: all 0.2s ease !important;
  display: inline-block !important;
  text-shadow: none !important;
}

.theme-dark .log-content .timestamp-highlight:hover,
.theme-dark pre .timestamp-highlight:hover,
.theme-dark .timestamp-highlight:hover,
.v-application.theme--dark .log-content .timestamp-highlight:hover,
.v-application.theme--dark pre .timestamp-highlight:hover,
.v-application.theme--dark .timestamp-highlight:hover {
  background-color: rgba(76, 175, 80, 0.25) !important;
  border-color: rgba(76, 175, 80, 0.6) !important;
  transform: scale(1.02) !important;
}

/* CSS Global para timestamps - força aplicação */
.timestamp-highlight {
  color: #4CAF50 !important;
  background-color: rgba(76, 175, 80, 0.15) !important;
  border-color: rgba(76, 175, 80, 0.4) !important;
  font-weight: 600 !important;
  font-family: Consolas, monospace !important;
  padding: 2px 4px !important;
  border-radius: 3px !important;
  border: 1px solid rgba(76, 175, 80, 0.4) !important;
  transition: all 0.2s ease !important;
  display: inline-block !important;
  text-shadow: none !important;
}

.timestamp-highlight:hover {
  background-color: rgba(76, 175, 80, 0.25) !important;
  border-color: rgba(76, 175, 80, 0.6) !important;
  transform: scale(1.02) !important;
}

/* CSS Global para [ERROR] - vermelho */
.error-highlight {
  color: #F44336 !important;
  background-color: rgba(244, 67, 54, 0.15) !important;
  border-color: rgba(244, 67, 54, 0.4) !important;
  font-weight: 600 !important;
  padding: 2px 4px !important;
  border-radius: 3px !important;
  border: 1px solid rgba(244, 67, 54, 0.4) !important;
  transition: all 0.2s ease !important;
  display: inline-block !important;
  text-shadow: none !important;
}

.error-highlight:hover {
  background-color: rgba(244, 67, 54, 0.25) !important;
  border-color: rgba(244, 67, 54, 0.6) !important;
  transform: scale(1.02) !important;
}

/* CSS Global para [DEBUG] - azul */
.debug-highlight {
  color: #2196F3 !important;
  background-color: rgba(33, 150, 243, 0.15) !important;
  border-color: rgba(33, 150, 243, 0.4) !important;
  font-weight: 600 !important;
  font-family: Consolas, monospace !important;
  padding: 2px 4px !important;
  border-radius: 3px !important;
  border: 1px solid rgba(33, 150, 243, 0.4) !important;
  transition: all 0.2s ease !important;
  display: inline-block !important;
  text-shadow: none !important;
}

.debug-highlight:hover {
  background-color: rgba(33, 150, 243, 0.25) !important;
  border-color: rgba(33, 150, 243, 0.6) !important;
  transform: scale(1.02) !important;
}

/* Melhor contraste para texto dos logs */
.log-content {
  filter: contrast(1.1);
}

.theme-dark .log-content {
  filter: contrast(1.2);
}

/* Estilos para o dialog moderno */
.modern-dialog .v-dialog {
  border-radius: 20px !important;
  overflow: hidden;
}

.modern-dialog-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px !important;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25) !important;
}

.modern-dialog-title {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  padding: 20px 24px !important;
  font-weight: 600 !important;
}

.close-btn {
  transition: all 0.3s ease;
  border-radius: 50% !important;
}

.close-btn:hover {
  background-color: rgba(244, 67, 54, 0.1) !important;
  transform: scale(1.1);
}

/* Tema escuro para o dialog */
.theme-dark .modern-dialog-card {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(45, 45, 45, 0.9) 100%) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5) !important;
}

.theme-dark .modern-dialog-title {
  background: linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: #ffffff !important;
}

.theme-dark .close-btn:hover {
  background-color: rgba(244, 67, 54, 0.2) !important;
}

/* Tema escuro para layout lado-a-lado */
.theme-dark .logs-side-by-side .log-section {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(45, 45, 45, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.theme-dark .logs-side-by-side .log-section:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
}

.theme-dark .logs-side-by-side .log-header {
  background: linear-gradient(135deg, #2d2d2d 0%, #3d3d3d 100%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: #ffffff;
}

.theme-dark .logs-side-by-side .log-content {
  background: linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%);
  color: #e0e0e0;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-x: auto;
  overflow-y: auto;
}

/* Melhorias no card-text */
.v-card__text {
  background: transparent !important;
  padding: 24px !important;
}

/* Animações de entrada para o dialog */
.modern-dialog .v-dialog {
  animation: dialogSlideIn 0.3s ease-out;
}

@keyframes dialogSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
</style> 